import { useState, useEffect, useMemo, useRef, forwardRef, useImperativeHandle } from 'react'
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { Virtuoso } from 'react-virtuoso'

import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import TableBody from '@mui/material/TableBody'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import Alert from '@mui/material/Alert'
import AddIcon from '@mui/icons-material/Add'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import CloudDownloadIcon from '@mui/icons-material/CloudDownload'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import DeleteIcon from '@mui/icons-material/Delete'
import IconButton from '@mui/material/IconButton'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Box from '@mui/material/Box'
import Skeleton from '@mui/material/Skeleton'
import Typography from '@mui/material/Typography'
import AutocompleteSelect from '../../../common/components/AutocompleteSelect'

import Papa from 'papaparse'

import { apiClient } from '~/api'

type Shop = { id: number; shop_name: string }

interface Props {
    shops?: Shop[]
    settings?: Record<string, unknown>
    items?: unknown[]
    env?: 'sandbox' | 'production'
}

type Row = {
    shop_id: number | ''
    sku: string
    item_id: string
    _key: string // stable key for React and identity mapping
    _isNew?: boolean // created/updated locally
    _isDeleted?: boolean // marked for deletion
    _errors?: { shop_id?: boolean; sku?: boolean; item_id?: boolean }
}

// Raw rows coming from API without local UI flags/keys
type APIRow = {
    shop_id: number
    sku: string
    item_id: string
}

// ----------------------------- Validation helper -----------------------------
const str_convertor = (v: unknown) => (typeof v === 'string' ? v : v === undefined || v === null ? '' : String(v))

const rowIsValid = (r: Row) => r.shop_id !== '' && r.sku.trim() && str_convertor(r.item_id).trim()

export interface RainforestItemsHandle {
    getDirtyRows: () => [number, string, string | null][]
    resetDirty: () => void
    validate: () => boolean
}

const RainforestItems = forwardRef<RainforestItemsHandle, Props>(({ shops = [], settings = {}, items = [], env = 'production' }, ref) => {

    /* -------------------------------- Fetch existing rows ------------------------------- */
    const [search, setSearch] = useState('')
    const [filterShopId, setFilterShopId] = useState<number | ''>('')

    const queryClient = useQueryClient()

    const fetchLimit = 25 // Backend uses default PageNumberPagination which ignores limit parameter and always returns 25

    /* ------------------------------- Bulk upload state ------------------------------- */
    const [bulkOpen, setBulkOpen] = useState(false)
    const [uploading, setUploading] = useState(false)
    const [uploadSuccess, setUploadSuccess] = useState(false)
    const [downloading, setDownloading] = useState(false)
    const fileInputRef = useRef<HTMLInputElement | null>(null)

    // Ref to the scroll container so we can programmatically scroll to top when adding a row
    const listRef = useRef<HTMLDivElement | null>(null)

    const fetchRows = async ({ pageParam = 1 }): Promise<{ next: string | null; results: APIRow[] }> => {
        const params = new URLSearchParams()
        params.append('limit', String(fetchLimit))
        params.append('page', String(pageParam))
        if (search) params.append('search', search)
        if (filterShopId !== '') params.append('shop_id', String(filterShopId))
        const res = await apiClient
            .get(`integrations/rainforest/${env}/items/?${params.toString()}`)
            .json<{
                next: string | null
                results: [number, string, string][]
            }>()
        return {
            next: res.next,
            results: res.results.map(([shop_id, sku, item_id]) => ({ shop_id, sku, item_id }))
        }
    }

    const {
        data,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isLoading
    } = useInfiniteQuery(['rainforest-items', env, search, filterShopId], fetchRows, {
        getNextPageParam: (lastPage) => {
            if (!lastPage.next) return undefined
            try {
                const url = new URL(lastPage.next, window.location.origin)
                const nextPageStr = url.searchParams.get('page')
                return nextPageStr ? Number(nextPageStr) : undefined
            } catch {
                return undefined
            }
        },
    })

    const existingRows: APIRow[] = useMemo(() => {
        return data?.pages.flatMap((p) => p.results) ?? []
    }, [data])

    /* --------------------------------- Local rows state -------------------------------- */
    const [rows, setRows] = useState<Row[]>([])
    const initialMapRef = useRef<Map<string, APIRow>>(new Map())
    const localKeyCounterRef = useRef(0)

    // When fetched rows change, initialise local state once
    useEffect(() => {
        // Whenever fetched rows change (e.g. after applying new filters),
        // sync local rows state so that the UI reflects the latest data.
        setRows(
            existingRows.map((r, i) => ({
                ...r,
                // create a stable key from content plus sequence to avoid collisions
                _key: `${r.shop_id}|${r.sku}|${r.item_id}|${i}`,
            }))
        )
        initialMapRef.current = new Map(
            existingRows.map((r) => [`${r.shop_id}|${r.sku}|${r.item_id}`, r])
        )
    }, [existingRows])

    // When changing the shop filter, clear state only when selecting All Connections from a specific shop
    const prevFilterShopIdRef = useRef<number | ''>(filterShopId)
    useEffect(() => {
        const prevFilterShopId = prevFilterShopIdRef.current
        prevFilterShopIdRef.current = filterShopId
        
        // Only clear state when switching FROM a specific shop TO All Connections
        // This preserves the "All Connections" data when it's already selected
        if (filterShopId === '' && prevFilterShopId !== '') {
            setRows([])
            if (listRef.current) {
                listRef.current.scrollTo({ top: 0 })
            }
            queryClient.removeQueries({ queryKey: ['rainforest-items', env], exact: false })
        }
    }, [filterShopId, env, queryClient])

    /* ---------------------------------- Handlers --------------------------------------- */
    const handleBulkOpen = () => {
        setUploadSuccess(false)
        setBulkOpen(true)
    }

    const handleBulkClose = () => {
        if (uploading) return // prevent close while uploading
        setBulkOpen(false)
    }

    const parseAndUpload = async (file: File) => {
        setUploading(true)
        try {
            const text = await file.text()
            const parsed = Papa.parse<string[]>(text.trim(), { skipEmptyLines: true })
            if (parsed.errors.length) {
                console.error(parsed.errors)
                throw new Error('Failed to parse CSV')
            }
            const uploadRows: [number, string, string][] = (parsed.data as string[][]).map((row: string[]) => [Number(row[0]), row[1], row[2]])
            await apiClient.post(`integrations/rainforest/${env}/items/`, { json: uploadRows })
            setUploadSuccess(true)
            // refresh list
            void queryClient.invalidateQueries(['rainforest-items', env])
        } catch (err) {
            console.error(err)
            alert('Upload failed')
        } finally {
            setUploading(false)
        }
    }

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (file) {
            void parseAndUpload(file)
        }
    }

    const downloadCSV = async () => {
        setDownloading(true)
        try {
            // Fetch all records by making multiple API calls with timeout protection
            let allRows: APIRow[] = []
            let page = 1
            let hasNext = true
            const maxPages = 1000 // Safety limit: max 1000 pages (25,000 records)
            const timeoutMs = 30000 // 30 seconds timeout
            
            const startTime = Date.now()
            
            while (hasNext && page <= maxPages) {
                // Check timeout
                if (Date.now() - startTime > timeoutMs) {
                    throw new Error('Download timeout: operation took too long')
                }
                
                const params = new URLSearchParams()
                params.append('limit', String(fetchLimit))
                params.append('page', String(page))
                if (search) params.append('search', search)
                if (filterShopId !== '') params.append('shop_id', String(filterShopId))
                
                const res = await apiClient
                    .get(`integrations/rainforest/${env}/items/?${params.toString()}`)
                    .json<{
                        next: string | null
                        results: [number, string, string][]
                    }>()
                
                allRows = [...allRows, ...res.results.map(([shop_id, sku, item_id]) => ({ shop_id, sku, item_id }))]
                hasNext = res.next !== null
                page++
            }
            
            if (page > maxPages) {
                throw new Error('Download limit exceeded: too many records')
            }
            
            // Convert to CSV format
            const csvData = allRows.map(row => [row.shop_id, row.sku, row.item_id])
            const csv = Papa.unparse(csvData)
            
            // Create download link
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
            const link = document.createElement('a')
            const url = URL.createObjectURL(blob)
            link.setAttribute('href', url)
            link.setAttribute('download', `items_${env}_${new Date().toISOString().split('T')[0]}.csv`)
            link.style.visibility = 'hidden'
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
        } catch (err) {
            console.error('Failed to download CSV:', err)
            const errorMessage = err instanceof Error ? err.message : 'Download failed'
            alert(errorMessage)
        } finally {
            setDownloading(false)
        }
    }

    const handleAdd = () => {
        const nextKey = `local-${localKeyCounterRef.current++}`
        setRows((prev) => [{ shop_id: '', sku: '', item_id: '', _key: nextKey, _isNew: true }, ...prev])
        if (listRef.current) {
            listRef.current.scrollTo({ top: 0, behavior: 'smooth' })
        }
    }

    const handleDelete = (idx: number) => {
        setRows((prev) =>
            prev.map((r, i) => (i === idx ? { ...r, _isDeleted: true, _isNew: false } : r)),
        )
    }

    const handleChange = <K extends keyof Row>(idx: number, field: K, val: Row[K]) => {
        setRows((prev) =>
            prev.map((r, i) => {
                if (i !== idx) return r
                const newRow = { ...r, [field]: val, _isNew: true }
                if (newRow._errors) {
                    newRow._errors[field as keyof typeof newRow._errors] = val === '' || (typeof val === 'string' && val.trim() === '')
                }
                return newRow
            }),
        )
    }

    const shopOptions: [string, number][] = shops.map((s) => [s.shop_name, s.id])

    const renderSelect = (
        idx: number,
        field: keyof Omit<Row, 'sku' | 'item_id'>,
        options: [string, string | number][],
        hasError: boolean,
    ) => (
        <TextField
            select
            variant="outlined"
            color="secondary"
            size="small"
            margin="none"
            error={hasError}
            value={rows[idx][field] as unknown}
            onChange={(e) => handleChange(idx, field as keyof Row, e.target.value as unknown as Row[keyof Row])}
            fullWidth
            sx={{
                '& .MuiOutlinedInput-root': { p: '1px 6px', height: 26 },
                '& .MuiOutlinedInput-input': { p: 0, fontSize: '0.8125rem' },
                '& .MuiSelect-select': { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block', fontSize: '0.8125rem' }
            }}
        >
            {options.map(([name, id]) => (
                <MenuItem key={id} value={id} sx={{ fontSize: '0.8125rem', py: 0.5 }}>
                    {name}
                </MenuItem>
            ))}
        </TextField>
    )

    /* --------------------------- Netsuite items options --------------------------- */
    const netsuiteItems: [string, string][] = useMemo(() => {
        const source = Array.isArray(items) && items.length ? items : (settings.items as unknown[]) || []
        const mapped: [string, string][] = (source as unknown[]).flatMap((item) => {
            if (item && typeof item === 'object' && !Array.isArray(item)) {
                const obj = item as Record<string, unknown>
                const label = String(obj.name ?? obj.label ?? '')
                const value = String(obj.id ?? obj.value ?? '')
                return label ? [[label, value]] : []
            }
            return []
        })
        return mapped
    }, [items, settings])

    /* --------------------------- Render item_id select --------------------------- */
    const renderItemSelect = (idx: number, hasError: boolean) => {
        const optionObjs = netsuiteItems.map(([label, value]) => ({ label, value }))
        return (
            <AutocompleteSelect
                options={optionObjs}
                value={rows[idx].item_id}
                onChange={(val) => handleChange(idx, 'item_id', val as string)}
                fullWidth
                error={hasError}
            />
        )
    }

    /* -------------------------------- Infinite scroll ---------------------------------- */
    const onScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const target = e.currentTarget
        if (hasNextPage && !isFetchingNextPage && target.scrollHeight - target.scrollTop - target.clientHeight < 200) {
            fetchNextPage()
        }
    }

    /* ----------------------------------- Filters --------------------------------------- */
    const handleClearFilters = () => {
        setSearch('')
        setFilterShopId('')
    }

    const validateRows = (): boolean => {
        let allValid = true
        setRows((prev) =>
            prev.map((r) => {
                if (r._isDeleted) return r // skip deleted rows
                const errors = {
                    shop_id: r.shop_id === '',
                    sku: r.sku.trim() === '',
                    item_id: str_convertor(r.item_id).trim() === '',
                }
                if (errors.shop_id || errors.sku || errors.item_id) {
                    allValid = false
                }
                return { ...r, _errors: errors }
            }),
        )
        return allValid
    }

    useImperativeHandle(ref, () => ({
        validate: validateRows,
        getDirtyRows: () =>
            rows
                .filter(
                    (r) =>
                        (
                            r._isNew && rowIsValid(r)
                        ) ||
                        (r._isDeleted && r.shop_id !== '' && r.sku.trim())
                )
                .map((r) => [
                    Number(r.shop_id),
                    r.sku.trim(),
                    r._isDeleted ? null : str_convertor(r.item_id).trim(),
                ] as [number, string, string | null]),
        resetDirty: () =>
            setRows((prev) => prev.filter((r) => !r._isDeleted).map((r) => ({ ...r, _isNew: false }))),
    }), [rows])

    // Visible rows with their original indices preserved to keep handlers aligned
    const visibleRows = useMemo(() =>
        rows.map((r, i) => ({ row: r, idx: i })).filter(({ row }) => !row._isDeleted)
    , [rows])

    return (
        <Paper elevation={0} sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <TextField
                    select
                    label="Connection"
                    InputLabelProps={{
                        sx: {
                            fontSize: '0.75rem',
                            lineHeight: 1.2,
                            // default (not-shrunk) position for small dense field
                            '&.MuiInputLabel-sizeSmall': {
                                transform: 'translate(10px, 8px) scale(1)',
                            },
                            // when shrunk (focused/filled)
                            '&.MuiInputLabel-shrink': {
                                transform: 'translate(8px, -6px) scale(0.85)',
                            },
                        },
                    }}
                    size="small"
                    color="secondary"
                    value={filterShopId}
                    onChange={(e) => setFilterShopId(e.target.value === '' ? '' : Number(e.target.value))}
                    sx={{
                        minWidth: 160,
                        '& .MuiOutlinedInput-root': { p: '1px 6px', height: 26 },
                        '& .MuiOutlinedInput-input': { p: 0, fontSize: '0.8125rem' },
                        '& .MuiSelect-select': { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block', fontSize: '0.8125rem' }
                    }}
                >
                    <MenuItem value="" sx={{ fontSize: '0.8125rem' }}>All Connections</MenuItem>
                    {shopOptions.map(([name, id]) => (
                        <MenuItem key={id} value={id} sx={{ fontSize: '0.8125rem', py: 0.5 }}>
                            {name}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField
                    label="Search SKU"
                    InputLabelProps={{
                        sx: {
                            fontSize: '0.75rem',
                            lineHeight: 1.2,
                            // default (not-shrunk) position for small dense field
                            '&.MuiInputLabel-sizeSmall': {
                                transform: 'translate(10px, 8px) scale(1)',
                            },
                            // when shrunk (focused/filled)
                            '&.MuiInputLabel-shrink': {
                                transform: 'translate(8px, -6px) scale(0.85)',
                            },
                        },
                    }}
                    size="small"
                    color="secondary"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{
                        width: 160,
                        '& .MuiOutlinedInput-root': { p: '1px 6px', height: 26 },
                        '& .MuiOutlinedInput-input': { p: 0, fontSize: '0.8125rem' }
                    }}
                />
                <Button variant="outlined" color="secondary" size="small" onClick={handleClearFilters}>
                    Clear
                </Button>

            </Stack>

            <Stack direction="row" justifyContent="flex-end" spacing={1}>
                <Button variant="outlined" color="secondary" size="small" startIcon={downloading ? <CircularProgress size={16} /> : <CloudDownloadIcon />} onClick={downloadCSV} disabled={!existingRows.length || downloading}>
                    {downloading ? 'Downloading...' : 'Download'}
                </Button>
                <Button variant="outlined" color="secondary" size="small" startIcon={<CloudUploadIcon />} onClick={handleBulkOpen}>
                    Bulk Upload
                </Button>
                <Button variant="outlined" color="secondary" size="small" startIcon={<AddIcon />} onClick={handleAdd}>
                    Add
                </Button>
            </Stack>

            <Dialog open={bulkOpen} onClose={handleBulkClose} maxWidth="sm" fullWidth>
                <DialogTitle>Bulk Upload</DialogTitle>
                <DialogContent sx={{ padding: "24px" }}>
                    <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1, fontSize: '0.875rem' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>File Format Instructions:</Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>• File must be CSV format with 3 columns (no header needed):</Typography>
                        <Typography variant="body2" component="div" sx={{ pl: 2, mb: 1 }}>
                          1. connection_id (Connection ID)<br/>
                          2. sku (Product SKU)<br/>
                          3. item_id (NetSuite Item ID)
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>• Mapping behavior:</Typography>
                        <Typography variant="body2" component="div" sx={{ pl: 2 }}>
                          • Updates existing mappings or creates new ones<br/>
                          • To remove a mapping, leave item_id field empty
                        </Typography>
                    </Box>
                    
                    {uploading ? (
                        <Box sx={{ textAlign: 'center' }}>
                            <CircularProgress />
                            <Typography variant="body2" sx={{ mt: 1 }}>Uploading...</Typography>
                        </Box>
                    ) : uploadSuccess ? (
                        <Stack alignItems="center" spacing={1}>
                            <CheckCircleIcon color="primary" fontSize="large" />
                            <Box>Successful upload</Box>
                        </Stack>
                    ) : (
                        <Button
                            variant="contained"
                            component="label"
                            startIcon={<CloudUploadIcon />}
                            disabled={uploading}
                            color="secondary"
                            fullWidth
                        >
                            Upload CSV
                            <input
                                type="file"
                                accept=".csv"
                                hidden
                                ref={fileInputRef}
                                onChange={handleFileChange}
                            />
                        </Button>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleBulkClose} disabled={uploading} color="secondary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Table */}
            {isLoading ? (
                <Box sx={{ mt: 1, maxHeight: 400, overflow: 'auto' }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', py: 0.75, px: 1, fontSize: '0.8125rem', width: 200 }}>Connection</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', py: 0.75, px: 1, fontSize: '0.8125rem', width: 200 }}>SKU</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', py: 0.75, px: 1, fontSize: '0.8125rem', width: 200 }}>Item Code</TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {[...Array(5)].map((_, idx) => (
                                <TableRow key={idx} sx={{ bgcolor: idx % 2 ? 'grey.50' : 'common.white' }}>
                                    <TableCell sx={{ px: 1, py: 0.75 }}><Skeleton variant="text" height={24} /></TableCell>
                                    <TableCell sx={{ px: 1, py: 0.75 }}><Skeleton variant="text" height={24} /></TableCell>
                                    <TableCell sx={{ px: 1, py: 0.75 }}><Skeleton variant="text" height={24} /></TableCell>
                                    <TableCell />
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>
            ) : rows.length === 0 ? (
                <Alert
                    severity="info"
                    variant="outlined"
                    sx={{
                        marginTop: '1rem',
                        color: 'text.secondary',
                        bgcolor: 'grey.100',
                        borderColor: 'grey.300',
                        '& .MuiAlert-icon': { color: 'text.secondary' },
                    }}
                >
                    No items yet
                </Alert>
            ) : (
                <Box ref={listRef} sx={{ mt: 1, maxHeight: 400, overflow: 'auto' }} onScroll={onScroll}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', py: 0.75, px: 1, fontSize: '0.8125rem', width: 200 }}>Connection</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', py: 0.75, px: 1, fontSize: '0.8125rem', width: 200 }}>SKU</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', py: 0.75, px: 1, fontSize: '0.8125rem', width: 200 }}>Item Code</TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {visibleRows.map(({ row, idx }, displayIdx) => (
                                <TableRow key={row._key} sx={{ bgcolor: displayIdx % 2 ? 'grey.50' : 'common.white' }}>
                                    <TableCell sx={{ px: 1, py: 0.75 }}>{renderSelect(idx, 'shop_id', shopOptions, Boolean(row._errors?.shop_id))}</TableCell>
                                    <TableCell sx={{ px: 1, py: 0.75 }}>
                                        <TextField
                                            variant="outlined"
                                            color="secondary"
                                            size="small"
                                            margin="none"
                                            value={row.sku}
                                            error={Boolean(row._errors?.sku)}
                                            onChange={(e) => handleChange(idx, 'sku', e.target.value)}
                                            fullWidth
                                            sx={{ '& .MuiOutlinedInput-root': { p: '1px 6px', height: 26 }, '& .MuiOutlinedInput-input': { p: 0, fontSize: '0.8125rem' } }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ px: 1, py: 0.75 }}>
                                        {renderItemSelect(idx, Boolean(row._errors?.item_id))}
                                    </TableCell>
                                    <TableCell sx={{ width: 96, py: 0.75, px: 1 }}>
                                        <IconButton color="inherit" sx={{ color: 'grey.500' }} onClick={() => handleDelete(idx)} size="small">
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {isFetchingNextPage && (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ py: 2 }}>
                                        <CircularProgress size={20} />
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </Box>
            )}
        </Paper>
    )
})

export default RainforestItems
